import type { CSSProperties } from 'react'

// ============================================================================
// THE BLOCK REMINDER PICKER — "notify me 10 minutes before this", Google-Calendar style.
//
// WHAT IT WRITES. `CalendarBlock.reminders` is minutes BEFORE the block starts, `0` meaning "at
// start"; the Worker fires one notification per offset (dedupe key `block:<id>:<minutes>`), so
// "the day before AND ten minutes before" is one intention expressed as two numbers rather than
// two blocks. The field is REPLACED on save, never merged — which is what makes removing the last
// row mean something.
//
// A NEW BLOCK GETS NONE. There is no default offset here and there is not one on the Worker
// either: picking 10 minutes for the owner would be the app inventing a number, which is the
// thing this codebase is least allowed to do (CLAUDE.md → product identity).
//
// WHY A NATIVE `<select>` AND NOT A POPOVER MENU. Every floating panel in this app goes through
// lib/popover.ts because the app runs at zoom 1.3 and naive rect maths drifts off-screen — so the
// cheapest correct answer is not to open a panel at all. A native select is placed by the browser
// itself, is keyboard- and screen-reader-correct for free, and on the phone it is the platform's
// own wheel. It also keeps this picker inside the composer's own dismiss handling: a custom menu
// would be a second popover inside a popover, and closing one would close both.
// ============================================================================

/** Four weeks, in minutes — the Worker's `MAX_REMINDER_MINUTES`, restated where it is enforced. */
export const MAX_REMINDER_MINUTES = 40320
/** The Worker refuses the 21st. The row that would have made it is not offered. */
export const MAX_REMINDERS_PER_BLOCK = 20

/** The offsets worth one press. Anything else is `Custom…` and a number of minutes. */
const PRESETS: { minutes: number; label: string }[] = [
  { minutes: 0, label: 'At start' },
  { minutes: 5, label: '5 minutes before' },
  { minutes: 10, label: '10 minutes before' },
  { minutes: 30, label: '30 minutes before' },
  { minutes: 60, label: '1 hour before' },
  { minutes: 1440, label: '1 day before' },
]

/**
 * ONE ROW OF THE PICKER, as the composer holds it.
 *
 * `key` exists because two rows can carry the same number — "10 minutes before" twice is a
 * pointless thing to ask for but not an invalid one, and keying on the value would make React
 * treat the second as the first. `custom` is a UI mode, not data: it is what keeps the number
 * field open after the owner picks Custom… and before a number has been typed.
 */
export interface ReminderRow {
  key: number
  minutes: number
  custom: boolean
}

let nextKey = 0

/** The block's stored offsets, as editable rows. A value nobody has a preset for opens custom. */
export function toReminderRows(minutes: readonly number[] | undefined): ReminderRow[] {
  return (minutes ?? []).map((value) => ({
    key: ++nextKey,
    minutes: value,
    custom: !PRESETS.some((preset) => preset.minutes === value),
  }))
}

/** The rows, as the field the API takes. Order is kept: it is the order they were added in. */
export function fromReminderRows(rows: readonly ReminderRow[]): number[] {
  return rows.map((row) => row.minutes)
}

/** A fresh row, defaulting to the one offset that needs no arithmetic to understand. */
export function newReminderRow(): ReminderRow {
  return { key: ++nextKey, minutes: 10, custom: false }
}

/**
 * TRUE WHEN SAVE MUST NOT GO. A custom row whose number is empty, fractional, negative or past
 * four weeks would be a 400 from the Worker with a sentence in it; catching it here means the
 * owner is told before the round trip rather than after it.
 */
export function remindersInvalid(rows: readonly ReminderRow[]): boolean {
  return rows.some(
    (row) =>
      !Number.isInteger(row.minutes) || row.minutes < 0 || row.minutes > MAX_REMINDER_MINUTES,
  )
}

export function ReminderPicker({
  rows,
  onChange,
}: {
  rows: ReminderRow[]
  onChange: (next: ReminderRow[]) => void
}) {
  const replace = (key: number, patch: Partial<ReminderRow>) =>
    onChange(rows.map((row) => (row.key === key ? { ...row, ...patch } : row)))

  return (
    <div className="flex flex-col gap-[7px]">
      <div className="text-[10px] font-semibold uppercase leading-none tracking-[0.08em] text-microcopy">
        Reminders
      </div>

      {rows.length === 0 && (
        <div className="text-[10.5px] font-normal leading-[1.4] text-microcopy">
          None. Nothing is sent about this block unless you add one.
        </div>
      )}

      {rows.map((row, index) => (
        <div key={row.key} className="flex items-center gap-2">
          <select
            aria-label={`Reminder ${index + 1}`}
            value={row.custom ? 'custom' : String(row.minutes)}
            onChange={(e) => {
              if (e.target.value === 'custom') {
                replace(row.key, { custom: true })
                return
              }
              replace(row.key, { custom: false, minutes: Number(e.target.value) })
            }}
            className="mo-field min-w-0 flex-1 rounded-button border border-line-2 bg-surface px-[8px] py-[6px] text-[11.5px] leading-none text-ink outline-none"
          >
            {PRESETS.map((preset) => (
              <option key={preset.minutes} value={String(preset.minutes)}>
                {preset.label}
              </option>
            ))}
            <option value="custom">Custom…</option>
          </select>

          {row.custom && (
            <>
              <input
                type="number"
                min={0}
                max={MAX_REMINDER_MINUTES}
                step={1}
                inputMode="numeric"
                aria-label={`Reminder ${index + 1} minutes before`}
                // Empty reads as NaN, which `remindersInvalid` catches and Save refuses on. It is
                // NOT coerced to 0: "at start" is a real choice and a cleared field is not it.
                value={Number.isNaN(row.minutes) ? '' : String(row.minutes)}
                onChange={(e) =>
                  replace(row.key, {
                    minutes: e.target.value === '' ? Number.NaN : Number(e.target.value),
                  })
                }
                className="mo-field w-[70px] flex-none rounded-button border border-line-2 bg-surface px-[8px] py-[6px] text-[11.5px] leading-none text-ink outline-none"
              />
              <span className="flex-none text-[10.5px] leading-none text-microcopy">min before</span>
            </>
          )}

          <button
            type="button"
            aria-label={`Remove reminder ${index + 1}`}
            onClick={() => onChange(rows.filter((r) => r.key !== row.key))}
            style={{ '--mo-rule': 'var(--color-danger-2)' } as CSSProperties}
            className="mo-ink mo-press flex-none rounded-pill px-[9px] py-[5px] text-[11px] font-medium leading-none text-muted"
          >
            Remove
          </button>
        </div>
      ))}

      {rows.length < MAX_REMINDERS_PER_BLOCK ? (
        <button
          type="button"
          onClick={() => onChange([...rows, newReminderRow()])}
          style={{ '--mo-rule': 'var(--color-ink)' } as CSSProperties}
          className="mo-ink mo-press self-start rounded-pill px-[9px] py-[5px] text-[11px] font-medium leading-none text-ink-2"
        >
          Add reminder
        </button>
      ) : (
        // The affordance is ABSENT rather than disabled, and the reason is in words: twenty is
        // what the API accepts, so a twenty-first row could only ever be refused.
        <div className="text-[10.5px] font-normal leading-[1.4] text-microcopy">
          Twenty is the most one block can carry.
        </div>
      )}
    </div>
  )
}
